import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  signal,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { selectCurrentUser, selectIsPaid, User } from 'pl-core-services';
import { filter, finalize, map, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { PlAccountService } from '@common/entities/pl-account.service';
import { selectAccountId } from '@app/common/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { fromSubscription } from '@app/subscription/store';
import { UserProfileService } from '@app/common/services/user-profile.service';
import { LmsService } from '@app/common/services/lms.service';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import { buildRoomShareUrl } from '@app/common/room-share-url.utils';
import { FeatureFlagService } from '@app/common/services/feature-flag.service';
import { FeatureFlags } from '@common/constant';
import { ZendeskAuthService } from '@app/common/services/zendesk-auth.service';
import { ProviderBaaSignatureComponent } from '../provider-baa-signature/provider-baa-signature.component';
import {
  isAdobeSignEsignEvent,
  isAdobeSignOrigin,
  parseAdobeSignMessage,
} from '../provider-baa-signature/adobe-sign-message';

@Component({
  selector: 'pl-provider-workspace',
  templateUrl: './provider-workspace.component.html',
  styleUrls: ['./provider-workspace.component.scss'],
})
export class ProviderWorkspaceComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly setupUrl = environment.setup_url;

  user: User = {
    username: '',
    first_name: '',
  };

  isPaid = false;

  dialogRef: any;

  readonly lms_url = `${environment.django_login_url}/saml/sso/learnupon?sp=${environment.learnupon_url}&RelayState=Dashboard`;

  readonly isSiriusEnabled = getIsSiriusEnabled();

  isLmsLoading = signal(false);

  license$ = this.store.select(fromSubscription.selectCurrentLicenseName);

  canUpgrade$ = this.store.select(fromSubscription.selectCanUpgrade);

  showNewLanding = false;

  private currentUser: User;

  private baaSignSuccess = false;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private accountService: PlAccountService,
    private snackbar: MatSnackBar,
    private userService: UserProfileService,
    private lmsService: LmsService,
    private cdr: ChangeDetectorRef,
    private featureFlagService: FeatureFlagService,
    private zendeskAuthService: ZendeskAuthService
  ) {}

  ngOnInit(): void {
    this.featureFlagService
      .isFeatureEnabled(FeatureFlags.KANGA_LANDING_PAGE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        this.showNewLanding = enabled;
        this.cdr.detectChanges();
      });

    this.store
      .select(selectCurrentUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.currentUser = user;
        this.user = user;

        // LEGACY: Track first visit to Kanga by populating welcome_date.
        // The welcome modal was removed (PLAT-2546), but we maintain this field
        // for backward compatibility with downstream reporting/analytics systems.
        this.userService.getUserProfile().subscribe((profile) => {
          if (!profile.welcome_date) {
            const updatedProfile = { ...profile, welcome_shown: true };
            this.userService.updateUserProfile(updatedProfile);
          }
        });
      });
    this.store
      .select(selectIsPaid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isPaid) => {
        this.isPaid = isPaid;
      });

    this.onBaaSignSuccessHash();
    this.ensureBAASigned();
  }

  @HostListener('window:hashchange')
  onHashChange(): void {
    this.onBaaSignSuccessHash();
  }

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    if (!isAdobeSignOrigin(event.origin)) {
      return;
    }
    const payload = parseAdobeSignMessage(event.data);
    if (!isAdobeSignEsignEvent(payload)) {
      return;
    }
    this.dialogRef.close();
    window.location.hash = '#sign-success';
  }

  private ensureBAASigned() {
    this.store
      .select(selectAccountId)
      .pipe(
        filter((accountId) => !!accountId),
        switchMap((accountId) =>
          this.accountService
            .isAccountBaaSigned(accountId)
            .pipe(map((baaSigned) => [baaSigned, accountId] as const))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([baaSigned, accountId]) => {
        if (!baaSigned && !this.baaSignSuccess) {
          this.openBaaDialog(accountId);
        }
      });
  }

  private onBaaSignSuccessHash(): void {
    if (window.location.hash === '#sign-success') {
      this.baaSignSuccess = true;
      this.snackbar.open(
        'A copy will be emailed to you after you confirm your email in your mailbox',
        'X',
        {
          verticalPosition: 'top',
          panelClass: ['baa-sign-success-snackbar'],
        }
      );
    }
  }

  get roomUrl(): string {
    return buildRoomShareUrl(this.user?.username ?? '');
  }

  copyRoomUrl() {
    navigator.clipboard.writeText(this.roomUrl);
  }

  openBaaDialog(accountId: string) {
    this.dialogRef = this.dialog.open(ProviderBaaSignatureComponent, {
      minWidth: '1000px',
      height: '85%',
      disableClose: true,
      closeOnNavigation: false,
      data: {
        userUuid: this.user.uuid,
        accountUuid: accountId,
      },
    });
  }

  openSupportChat() {
    this.zendeskAuthService.openSupportChat();
  }

  openLmsPortal() {
    if (this.isLmsLoading()) {
      return;
    }
    this.isLmsLoading.set(true);

    this.lmsService
      .openPortal()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLmsLoading.set(false))
      )
      .subscribe({
        error: () => {
          this.snackbar.open('Unable to access Telehealth Institute', 'Close', {
            duration: 5000,
          });
        },
      });
  }
}
